"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.rejectBillDiscountRequestSchema = exports.approveBillDiscountRequestSchema = exports.approvalIdParamSchema = exports.consultationConcessionOfferQuerySchema = exports.staffConcessionEmployeeQuerySchema = exports.applyStaffDiscountRequestSchema = exports.STAFF_BENEFICIARY_CATEGORIES = exports.requestBillDiscountRequestSchema = exports.listOpBillsQuerySchema = exports.opBillDoctorQuerySchema = exports.opBillChargeSearchQuerySchema = exports.opBillBillingContextQuerySchema = exports.opBillDashboardQuerySchema = exports.listOpBillDraftsQuerySchema = exports.collectOpBillPaymentRequestSchema = exports.printOpBillRequestSchema = exports.opBillPrintKindSchema = exports.priceOpBillRequestSchema = exports.postOpBillBodySchema = exports.postOpBillRequestSchema = exports.CREDIT_BILL_WITH_PAYMENT_MESSAGE = exports.rejectCreditBillWithPayment = exports.postOpBillRequestFields = exports.opBillCreditPartySchema = exports.opBillCreditPartyTypeSchema = exports.opBillPaymentLineSchema = exports.discardOpBillDraftRequestSchema = exports.resumeOpBillDraftRequestSchema = exports.holdOpBillDraftRequestSchema = exports.updateOpBillDraftRequestSchema = exports.priceOpBillPreviewRequestSchema = exports.createOpBillDraftRequestSchema = exports.opBillPendingBookingSchema = exports.opBillDraftLineSchema = void 0;
/**
 * Request contracts for the OP Billing Workstation — ADR-062 Phase 4.
 *
 * These mirror the FROZEN application-service contracts in
 * `backend/src/modules/financial/op-billing/op-bill.contracts.ts`. Changing a
 * shape here is a v2 decision, not a refactor (§6A.4).
 *
 * TWO THINGS ARE DELIBERATELY ABSENT, AND MUST STAY ABSENT
 *
 * 1. **No amount is ever an input.** No rate, unit price, tax, GST split,
 *    discount amount, invoice number or snapshot appears in any schema below.
 *    The client names WHAT to bill; the server decides what it costs
 *    (ADR-062 §C5 step 2, ADR-065). There is no field through which a browser
 *    can assert a price.
 * 2. **No orchestration.** One request maps to exactly one application-service
 *    command (§6A.4). There is no "post and refund" or "price and post" shape.
 *
 * Watch the silent-strip trap: a field missing from a schema is accepted by the
 * router and then dropped before it reaches the service. ECM Phase 1 lost six
 * columns that way. Every field the service reads MUST be listed here, and
 * covered by a round-trip test.
 */
const zod_1 = require("zod");
const open_value_pricing_1 = require("../../domain/open-value-pricing");
const staff_concession_1 = require("../../domain/staff-concession");
const lab_newborn_schema_1 = require("../../schemas/lab-newborn.schema");
const uuid = zod_1.z.string().uuid();
/**
 * One line of billing INTENT. Note there is no price field — that is the whole
 * point of the shape.
 */
exports.opBillDraftLineSchema = zod_1.z
    .object({
    /**
     * A CATALOGUE line: the server prices it from the charge's active price
     * version. Optional only because an ACCOUNT HEAD line is the alternative —
     * exactly one of the two is set, enforced below.
     */
    chargeId: uuid.optional(),
    /**
     * An ACCOUNT HEAD line — billing an income head directly, with an
     * operator-entered rate.
     *
     * THIS IS THE ONE PLACE A CLIENT SUPPLIES AN AMOUNT, and it is a deliberate,
     * recorded exception to "no amount is ever an input" (this file's header
     * rule 1). It exists because an account head has no price version anywhere:
     * it is a classification, so there is nothing for the server to resolve, and
     * the alternative was that heads could not be billed at all.
     *
     * WHAT THAT COSTS, stated plainly rather than discovered later:
     *   - no price history, no tariff, no payer rate, no package allowance and
     *     no margin check applies to such a line;
     *   - two operators may bill the same head at different rates on the same
     *     day, and nothing flags it;
     *   - the figure is whatever was typed, so the audit trail is the invoice
     *     line itself and the user who posted it.
     * Catalogue lines are unaffected — they still resolve server-side, and the
     * money-arithmetic fence still forbids the browser deriving any figure.
     */
    accountHeadId: uuid.optional(),
    /**
     * Paise, integer.
     *
     * Required on an ACCOUNT HEAD line. Also permitted on a CATALOGUE line, but
     * ONLY for an open-value service — one whose active price version is the ₹1
     * sentinel (see `open-value-pricing.ts`). Those services are priced at the
     * counter by design; before this, the line billed a literal ₹1.
     *
     * "Which price won?" stays unambiguous, which is what the old blanket
     * rejection was protecting: this schema cannot know what a charge resolves
     * to, so it only bounds the number. THE SERVER DECIDES whether an override
     * is allowed at all — `OpBillPricingService` re-resolves the charge and
     * rejects a rate on any service that is not open-value. A client cannot
     * reprice a normal service by sending a figure; the money-arithmetic fence
     * is intact.
     */
    unitRatePaise: zod_1.z.number().int().positive().max(100_000_000).optional(),
    /**
     * What the line is FOR, on an account head line. Free text, because the
     * head's own name ("CONS FEE - OP") is often not what the patient should
     * read on a bill. Falls back to the head's name when omitted.
     */
    description: zod_1.z.string().max(255).optional(),
    /**
     * Decimal because time-based and consumable charges legitimately bill in
     * fractions, even though lab and radiology bill in whole units. Positive:
     * a zero or negative quantity is V5, rejected server-side too.
     */
    quantity: zod_1.z.number().positive().max(100_000).optional(),
    /** Client-side row handle, echoed back so the UI can map results to rows. */
    lineRef: zod_1.z.string().max(64).optional(),
    /** A `User` id, NOT a `Doctor` id — `Doctor.id` matches zero rows here. */
    doctorId: uuid.optional(),
    /**
     * THE CATEGORY THIS ONE LINE IS PRICED AT — RADIOLOGY ONLY.
     *
     * Desk, 2026-09-09: "Op Bill screen show only C1 category, only radiology
     * require the changes of category so double click available to change."
     *
     * THIS REVERSES A RULE THIS FILE PREVIOUSLY ENFORCED BY ABSENCE. The
     * bill-level `chargeTier` below still carries the note explaining why there
     * was no per-line field: the desk's 2026-08-29 rule was "chosen once, the
     * whole bill follows, no changes of individual line item", and leaving the
     * field out was how that rule was made unexpressible. The desk has since
     * carved out radiology — an X-ray is priced by the category of the study
     * rather than the category of the visit — so the shape has to be able to
     * express it. The rule did not weaken; it acquired a stated exception.
     *
     * THE SCHEMA DOES NOT ENFORCE THE EXCEPTION, AND CANNOT. Whether a charge
     * is radiology depends on its identifier in the database, which this file
     * has no access to by design (it is pure, and shared with the browser). So
     * `OpBillPricingService` classifies the charge itself and IGNORES this on
     * anything that is not radiology — the same division of labour
     * `unitRatePaise` above already uses for open-value services: the schema
     * bounds the value, the server decides whether it applies.
     *
     * NOT REJECTED WHEN IT DOES NOT APPLY. A draft is re-synced wholesale, and
     * a charge re-coded out of the XR range would otherwise make an existing
     * bill unsavable over a field the operator never knowingly set.
     */
    chargeTier: zod_1.z.enum(['C1', 'C2', 'C3', 'C4']).optional(),
    remarks: zod_1.z.string().max(500).optional(),
})
    .superRefine((line, ctx) => {
    // EXACTLY ONE, never both and never neither. A line addressing both a
    // charge and a head would have two revenue destinations and two prices,
    // and whichever the code happened to read first would silently win.
    if (!line.chargeId === !line.accountHeadId) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'A line must name exactly one of chargeId or accountHeadId',
            path: ['chargeId'],
        });
        return;
    }
    if (line.accountHeadId && line.unitRatePaise === undefined) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'An account head line needs a rate — there is no price version to resolve one from',
            path: ['unitRatePaise'],
        });
    }
    // A catalogue line MAY carry a rate now, for open-value services only —
    // but never one at or below the sentinel. Re-sending ₹1 is the untouched
    // placeholder, not a decision, and it is exactly the figure this mechanism
    // exists to keep off an invoice. Whether the charge is open-value at all is
    // the server's call, not this schema's.
    if (line.chargeId && line.unitRatePaise !== undefined && !(0, open_value_pricing_1.isValidOpenValueRate)(line.unitRatePaise)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: open_value_pricing_1.OPEN_VALUE_RATE_MESSAGE,
            path: ['unitRatePaise'],
        });
    }
});
/**
 * A SLOT WAS PICKED, BUT NO APPOINTMENT EXISTS YET (desk, 2026-09-11).
 *
 * Mirrors `PendingBookingContract` in the backend's
 * `op-bill.contracts.ts` field for field — this is the wire shape for it.
 * Mutually exclusive with `appointmentId` below: that field names an
 * appointment that ALREADY EXISTS (a future booking); this one carries the
 * exact intent — department, doctor, date, slot, patient — for a visit where
 * a slot was picked but the business rule forbids writing a real
 * `Appointment` row before the bill is paid. Posting the bill is what
 * creates the row, for this EXACT slot, never a re-derived one.
 */
exports.opBillPendingBookingSchema = zod_1.z
    .object({
    branchId: uuid,
    patientId: uuid.optional(),
    draftRegistrationId: uuid.optional(),
    departmentId: uuid,
    doctorId: uuid,
    /**
     * ISO instant of the picked slot's date+time. A plain string, not
     * `z.coerce.date()` — this rides inside `OpBillDraft.payload.context` as
     * JSON and is read back as a string by `CreateAndConfirmOnBillingService`
     * regardless of what shape it is coerced to here, so keeping it a string
     * end to end avoids a wire type that disagrees with the backend's own
     * `PendingBookingContract.scheduledAt: string`.
     */
    scheduledAt: zod_1.z.string().min(1),
    /** The exact slot number picked. Never re-derived, never reassigned. */
    slotNumber: zod_1.z.number().int().min(1).max(500),
    bookingChannelId: uuid,
    consultationMode: zod_1.z.enum(['IN_PERSON', 'TELEMEDICINE', 'HOME_VISIT']).optional(),
    payerContext: zod_1.z.enum(['GENERAL', 'CORPORATE', 'INSURANCE', 'PACKAGE']).optional(),
    durationMinutes: zod_1.z.number().int().positive().optional(),
    /** Follow-up carve-out (RevisitTab) — resolved at confirm time, not before. */
    revisitType: zod_1.z.enum(['FOLLOW_UP']).optional(),
    parentAppointmentId: uuid.optional(),
    /**
     * `revisitWaived` IS DELIBERATELY ABSENT (desk, 2026-09-11) — same rule
     * `revisitBookingFieldsSchema` (`booking.request.ts`) already enforces for
     * every future booking: whether a follow-up is free is the server's
     * decision, computed by `RevisitAppointmentService.prepare()` at confirm
     * time. A client-supplied waiver here would be a browser granting itself
     * a free consultation.
     */
    /**
     * The standalone hold acquired the moment the slot was picked
     * (`POST /slots/hold`), protecting it while the bill is raised and paid.
     * Its absence is legal (an entry point not yet wired to acquire one) but
     * means there is nothing to validate before creating the appointment.
     */
    holdId: uuid.optional(),
    holdToken: zod_1.z.string().min(1).optional(),
})
    .refine((value) => Boolean(value.patientId) !== Boolean(value.draftRegistrationId), {
    message: 'Supply exactly one of patientId or draftRegistrationId',
    path: ['patientId'],
});
exports.createOpBillDraftRequestSchema = zod_1.z.object({
    branchId: uuid,
    patientId: uuid.optional(),
    /**
     * A WALK-IN BILL — someone who is not a patient and never will be.
     *
     * The desk types `99999` in the UHID box for a relative buying a report
     * copy or a passer-by paying for a dressing. Registering them would put a
     * person in the patient master with no clinical record, so the bill carries
     * the name instead and nothing else in the system learns it.
     *
     * Required when there is no `patientId`; the database enforces the same rule
     * on the posted invoice. The mobile is optional — a walk-in often will not
     * give one, and a bill must not be blocked on it.
     */
    walkInName: zod_1.z.string().trim().min(1).max(255).optional(),
    walkInMobile: zod_1.z.string().trim().max(20).optional(),
    /** Optional, same posture as `walkInMobile` — free text, no fixed set. */
    walkInGender: zod_1.z.string().trim().max(20).optional(),
    /** A raw age in years, not a DOB — see `FinInvoice.walkInAgeYears`. */
    walkInAgeYears: zod_1.z.number().int().min(0).max(150).optional(),
    /**
     * A NEWBORN'S TEST, BILLED TO THE MOTHER (desk, 2026-10-06). The baby is never
     * registered, so `patientId` is the MOTHER's — the bill sits on her OP number —
     * and these three say whose test it really is. All three or none; refused on a
     * walk-in bill (a baby's bill must sit on a registered OP number). The posted
     * invoice carries them to the lab order, which prints them as the patient.
     * See `domain/lab-newborn.ts`.
     */
    ...lab_newborn_schema_1.newbornFieldsShape,
    doctorId: uuid.optional(),
    /**
     * "OTHERS" — NO DOCTOR WAS INVOLVED IN THIS TRANSACTION (desk, 2026-10-13).
     *
     * The counter's doctor box is required, so "no doctor" has to be an ANSWER, not an
     * empty field — and a held bill has to remember that answer, or a resumed bill
     * comes back asking again (and the appointment's doctor fills the gap). The invoice
     * has always allowed a null doctor; this only records, on the draft, that null was
     * chosen. Never together with `doctorId` (refused by the command, not by this
     * schema, which is also the base of `.pick()` below).
     */
    noDoctor: zod_1.z.boolean().optional(),
    departmentId: uuid.optional(),
    payerId: uuid.optional(),
    counterId: uuid.optional(),
    cashSessionId: uuid.optional(),
    /**
     * THE APPOINTMENT THIS CONSULTATION BILL IS FOR.
     *
     * Desk, 2026-09-09: "create a rule where the cons op billed then only slot
     * should be booked... in all conditions."
     *
     * WITHOUT THIS THE RULE CANNOT EXIST. `FinInvoice.appointment_id` has been on
     * the table all along and was NULL on all 557 rows, because nothing ever sent
     * one — so "the consultation has been billed" was a fact the system could not
     * answer about any particular appointment. Carrying it here is what lets
     * posting promote that appointment's requested slot into a real hold.
     *
     * OPTIONAL, because most bills are not consultations. A counter bill for a
     * dressing or a report copy has no appointment, and requiring one would break
     * every other bill to serve this rule.
     */
    appointmentId: uuid.optional(),
    /**
     * A slot was picked and no appointment exists yet — see
     * `opBillPendingBookingSchema`. Mutually exclusive with `appointmentId`
     * above (enforced by the `.refine()` below): a bill either belongs to an
     * appointment that already exists, or carries the intent to create one on
     * payment, never both.
     */
    pendingBooking: exports.opBillPendingBookingSchema.optional(),
    /**
     * THE CATEGORY THIS WHOLE BILL IS PRICED AT — C1..C4 (ADR-082).
     *
     * Desk, 2026-08-29: chosen once, and the entire bill follows it — "no
     * changes of individual line item". This was for a long time a bill-level
     * field and NOT one on `opBillDraftLineSchema`, precisely so a bill with
     * lines at different categories could not be expressed.
     *
     * SINCE 2026-09-09 THAT HOLDS FOR EVERYTHING EXCEPT RADIOLOGY, which the desk
     * carved out — see `chargeTier` on `opBillDraftLineSchema`. This field is
     * still what every other line follows and still the only category the
     * operator chooses on screen; the exception is one family, decided by the
     * server from the charge's own identifier.
     *
     * OMITTED MEANS C1. `TIER_RATE_TYPE` maps C1 to the unconditional STANDARD
     * rate type every OP bill already resolved to, so leaving this out reproduces
     * the previous behaviour exactly.
     */
    chargeTier: zod_1.z.enum(['C1', 'C2', 'C3', 'C4']).optional(),
    lines: zod_1.z.array(exports.opBillDraftLineSchema).max(200).default([]),
}).refine((value) => !(value.appointmentId && value.pendingBooking), {
    message: 'Supply appointmentId or pendingBooking, never both — a bill belongs to an existing appointment or carries an intent to create one, not both',
    path: ['pendingBooking'],
});
/**
 * PRICE THESE LINES WITHOUT SAVING A DRAFT (`POST /op-billing/price-preview`).
 *
 * The counter used to create a server draft the moment a patient and a first
 * service were on screen, only because pricing resolves against a persisted
 * draft. Every bill that was then abandoned left a draft behind. This is the
 * same question - "what would these lines cost, for this patient, doctor and
 * category?" - asked without storing anything, so a draft can wait until the
 * operator actually commits (Preview, Hold, Save draft, concession, discount,
 * Post).
 *
 * DERIVED FROM THE DRAFT SCHEMA BY `.pick()`, NOT RESTATED. The preview must
 * accept exactly what the draft accepts for these fields, with the same
 * validators; a second hand-written copy is how a preview starts pricing a bill
 * the draft would have refused. The appointment and slot-intent fields are left
 * out on purpose: they decide what posting does with an appointment, not what
 * a line costs.
 */
exports.priceOpBillPreviewRequestSchema = exports.createOpBillDraftRequestSchema.innerType().pick({
    branchId: true,
    patientId: true,
    walkInName: true,
    walkInMobile: true,
    walkInGender: true,
    walkInAgeYears: true,
    doctorId: true,
    departmentId: true,
    payerId: true,
    counterId: true,
    chargeTier: true,
    lines: true,
});
exports.updateOpBillDraftRequestSchema = zod_1.z.object({
    lines: zod_1.z.array(exports.opBillDraftLineSchema).max(200),
    /**
     * THE CATEGORY THIS WHOLE BILL IS PRICED AT — C1..C4 (ADR-082).
     *
     * Desk, 2026-08-29: chosen once, and the entire bill follows it — "no
     * changes of individual line item". This was for a long time a bill-level
     * field and NOT one on `opBillDraftLineSchema`, precisely so a bill with
     * lines at different categories could not be expressed.
     *
     * SINCE 2026-09-09 THAT HOLDS FOR EVERYTHING EXCEPT RADIOLOGY, which the desk
     * carved out — see `chargeTier` on `opBillDraftLineSchema`. This field is
     * still what every other line follows and still the only category the
     * operator chooses on screen; the exception is one family, decided by the
     * server from the charge's own identifier.
     *
     * OMITTED MEANS C1. `TIER_RATE_TYPE` maps C1 to the unconditional STANDARD
     * rate type every OP bill already resolved to, so leaving this out reproduces
     * the previous behaviour exactly.
     */
    chargeTier: zod_1.z.enum(['C1', 'C2', 'C3', 'C4']).optional(),
    /**
     * THE DOCTOR RIDES EVERY SYNC (desk, 2026-10-13) — one of `doctorId` or `noDoctor: true`, or neither.
     *
     * A draft's doctor used to be written ONCE, when the draft was created, and an update carried no doctor
     * at all: change the doctor after Preview and the bill still posted to the first one. Both are applied
     * here. Neither sent = the screen has no opinion yet, and the draft's doctor is left as it is (an older
     * client, or a bill whose doctor was cleared). Both sent is refused by the command.
     */
    doctorId: uuid.optional(),
    noDoctor: zod_1.z.boolean().optional(),
    /**
     * A newborn's test billed to the mother — all three or none, `null` clears, absent leaves the draft's as it is.
     * Sent with every sync from the Laboratory desk (like `chargeTier`), because the desk can tick the box after the
     * first line is on the bill. See `domain/lab-newborn.ts`.
     */
    ...lab_newborn_schema_1.newbornFieldsUpdateShape,
    /** CAS guard (ADR-063). A mismatch is 409, never a silent merge. */
    expectedLockVersion: zod_1.z.number().int().positive(),
});
exports.holdOpBillDraftRequestSchema = zod_1.z.object({
    /** "Why is this bill parked?" is the first question at shift handover. */
    holdReason: zod_1.z.string().min(1).max(255),
    expectedLockVersion: zod_1.z.number().int().positive(),
});
exports.resumeOpBillDraftRequestSchema = zod_1.z.object({
    expectedLockVersion: zod_1.z.number().int().positive(),
});
exports.discardOpBillDraftRequestSchema = exports.resumeOpBillDraftRequestSchema.extend({
    /**
     * A draft with a discount request waiting (or approved and not yet used) is NOT
     * discarded silently: the server answers 409 `FIN_OP_DRAFT_HAS_APPROVALS`, so
     * the discount is never orphaned. Send `true` only after the operator has been
     * told the request will be withdrawn; it is then closed (EXPIRED) with the
     * draft and both are audited.
     */
    withdrawApprovals: zod_1.z.boolean().optional(),
});
exports.opBillPaymentLineSchema = zod_1.z.object({
    paymentModeId: uuid,
    /**
     * Paise, integer. This is the ONE amount a client supplies, and it is what
     * the patient handed over — not a price. The server still rejects a total
     * exceeding the bill.
     */
    amountPaise: zod_1.z.number().int().nonnegative(),
    referenceNumber: zod_1.z.string().max(100).optional(),
    /**
     * The bank account this tender settles into. Required for every non-cash
     * mode; forbidden for CASH.
     *
     * NOT ENFORCED HERE, DELIBERATELY. The mode arrives as an id, and whether it
     * is cash is a property of the `lookup_values` row behind that id — which
     * this schema cannot read. Enforcing a shape the schema can only guess at
     * would produce a rule that is wrong for any tenant whose modes differ.
     * `SettlementAccountResolver` holds the rule instead, and it is the same
     * code that resolves the GL leg, so the validation and the posting cannot
     * disagree about what a mode is.
     */
    bankAccountId: uuid.optional(),
});
exports.opBillCreditPartyTypeSchema = zod_1.z.enum(['EMPLOYEE', 'COMPANY']);
/**
 * Payment Mode: Employee Credit / Company Credit — who owes this bill.
 *
 * The pharmacy workstation's two credit modes, ported to OP billing. It is
 * NOT a payment mode in the `FINANCE/PAYMENT_MODE` lookup sense, and calling
 * it one would be a lie the accounting has to live with: no money changes
 * hands. It says the bill is posted on credit and names the party who will
 * settle it, which is why it is a field of its own rather than another
 * `paymentModeId`.
 *
 * `.refine` rather than a discriminated union because the browser sends the
 * field it has: EMPLOYEE needs `employeeId`, COMPANY needs `payerId`, and
 * sending both is a client bug worth a 422 rather than a silent preference for
 * one of them.
 */
exports.opBillCreditPartySchema = zod_1.z
    .object({
    partyType: exports.opBillCreditPartyTypeSchema,
    employeeId: uuid.optional(),
    payerId: uuid.optional(),
})
    .refine((value) => (value.partyType === 'EMPLOYEE' ? !!value.employeeId : !!value.payerId), {
    message: 'employeeId is required for EMPLOYEE credit, payerId for COMPANY credit',
    path: ['partyType'],
})
    .refine((value) => !(value.employeeId && value.payerId), {
    message: 'Send employeeId or payerId, never both — the bill is owed by exactly one party',
    path: ['partyType'],
});
/**
 * The FIELDS, unrefined.
 *
 * Exported separately because the router validates the body with `draftId`
 * omitted (the path supplies it), and `.omit()` exists on a `ZodObject` but not
 * on the `ZodEffects` a `.refine()` produces. Keeping the object addressable is
 * what lets the route derive its body schema from this one declaration instead
 * of restating the fields — the drift this file exists to prevent.
 */
exports.postOpBillRequestFields = zod_1.z.object({
    /**
     * Draft identity IS the idempotency key (§C5, Gate 7). A retry after a
     * timeout re-posts the same draft and receives the same invoice back.
     */
    draftId: uuid,
    /** Omitted or empty = a credit bill: invoice raised, nothing collected now. */
    payments: zod_1.z.array(exports.opBillPaymentLineSchema).max(10).optional(),
    /**
     * Present = this credit bill is owed by a named employee or company
     * rather than by the patient. Mutually exclusive with `payments`.
     */
    creditParty: exports.opBillCreditPartySchema.optional(),
    /** Warning codes the operator explicitly confirmed (e.g. DUPLICATE_SERVICE). Audited. */
    acknowledgedWarnings: zod_1.z.array(zod_1.z.string().max(64)).max(20).optional(),
    /**
     * REPEAT-BILL GUARD - opt in by sending this object.
     *
     * Present: the server refuses to post when the same patient (or the same
     * walk-in name and mobile) was already billed for the same doctor and the same
     * services within the last 30 minutes, answering 409
     * `FIN_OP_BILL_REPEAT_SUSPECTED` with the earlier bill(s). The operator can
     * then post anyway by sending the same request again with `reason` - the
     * reason is audited against the bill.
     *
     * Absent: nothing changes, so a client that has no way to ask the operator
     * (anything but the New OP Bill screen today) is not blocked by a question it
     * cannot show.
     */
    repeatBillCheck: zod_1.z
        .object({
        // A meaningful reason, not "ok": the server applies the same floor to the trimmed text.
        reason: zod_1.z.string().trim().min(10).max(200).optional(),
    })
        .optional(),
});
/**
 * Taking cash AND booking the bill to an employee would record the same rupees
 * as both collected and owed. Rejected at the edge, so no downstream layer has
 * to decide which one the operator meant.
 *
 * Applied as a named function so the ROUTE's derived body schema carries the
 * identical rule — a guard that exists on the full schema but not on the shape
 * actually validated over HTTP is a guard that does not exist.
 */
const rejectCreditBillWithPayment = (value) => !(value.creditParty && (value.payments?.length ?? 0) > 0);
exports.rejectCreditBillWithPayment = rejectCreditBillWithPayment;
exports.CREDIT_BILL_WITH_PAYMENT_MESSAGE = 'A credit bill cannot also carry a payment — collect the balance later instead';
exports.postOpBillRequestSchema = exports.postOpBillRequestFields.refine(exports.rejectCreditBillWithPayment, {
    message: exports.CREDIT_BILL_WITH_PAYMENT_MESSAGE,
    path: ['creditParty'],
});
/** What the ROUTER enforces: the same fields and the same rule, minus the path param. */
exports.postOpBillBodySchema = exports.postOpBillRequestFields
    .omit({ draftId: true })
    .refine(exports.rejectCreditBillWithPayment, {
    message: exports.CREDIT_BILL_WITH_PAYMENT_MESSAGE,
    path: ['creditParty'],
});
exports.priceOpBillRequestSchema = zod_1.z.object({ draftId: uuid });
exports.opBillPrintKindSchema = zod_1.z.enum(['BILL', 'RECEIPT', 'DUPLICATE', 'ESTIMATE']);
exports.printOpBillRequestSchema = zod_1.z
    .object({
    kind: exports.opBillPrintKindSchema,
    reason: zod_1.z.string().max(255).optional(),
    counterId: uuid.optional(),
})
    .refine((value) => value.kind !== 'DUPLICATE' || (value.reason?.trim().length ?? 0) > 0, {
    // A duplicate print without a reason is exactly the audit hole
    // `op_bill_print_logs` exists to close.
    message: 'A reason is required when printing a DUPLICATE',
    path: ['reason'],
});
/**
 * Collect payment against an already-posted invoice.
 *
 * Distinct from `opBillPaymentLineSchema`: that one may carry a zero amount
 * (a post with no collection is a credit bill), whereas collecting nothing is
 * not a collection — so `amountPaise` is strictly positive here and at least
 * one line is required.
 */
exports.collectOpBillPaymentRequestSchema = zod_1.z.object({
    /**
     * DERIVED from `opBillPaymentLineSchema`, not restated.
     *
     * It used to be an inline copy of the same three fields, and that copy is
     * exactly how `bankAccountId` went missing on this endpoint: the field was
     * added to the shared line schema, the browser sent it, and Zod stripped it
     * here because this object had never heard of it. A stripped field is the
     * worst kind — the request succeeds, and the money settles somewhere nobody
     * chose.
     *
     * The only real difference is the amount: collecting against an existing
     * invoice requires a POSITIVE line (a zero-rupee collection is not a
     * collection), whereas posting tolerates zero. So that one field is
     * overridden and everything else is inherited.
     */
    payments: zod_1.z
        .array(exports.opBillPaymentLineSchema.extend({ amountPaise: zod_1.z.number().int().positive() }))
        .min(1)
        .max(10),
    cashSessionId: uuid.optional(),
});
/** Drafts are listed per counter — a cashier resumes their own held bills, not the ward's. */
exports.listOpBillDraftsQuerySchema = zod_1.z.object({
    counterId: uuid.optional(),
});
/**
 * Shared by all seven dashboard endpoints. One schema rather than seven because
 * every card answers the same question — "over which window?" — and only the
 * aggregation differs (Dashboard Rule 2 separates the QUERIES, not the window).
 */
exports.opBillDashboardQuerySchema = zod_1.z.object({
    date: zod_1.z.string().date().optional(),
    days: zod_1.z.coerce.number().int().positive().max(365).optional(),
    limit: zod_1.z.coerce.number().int().positive().max(50).optional(),
});
/** Where and how you are billing — never what (Billing Context Rule 2). */
exports.opBillBillingContextQuerySchema = zod_1.z.object({
    branchId: uuid.optional(),
    counterId: uuid.optional(),
});
/**
 * Charge search input. Note what CANNOT be asked for: no payer, no package, no
 * effective date — because the response carries no price to qualify (Search
 * Rule: discoverability, never commercial truth).
 */
exports.opBillChargeSearchQuerySchema = zod_1.z.object({
    q: zod_1.z.string().max(100).optional(),
    /**
     * Which desk is asking — desk, 2026-08-28: a "999" ward service "should be
     * visible under IP Billing only".
     *
     * OMITTED MEANS UNFILTERED, which is what every maintenance screen and report
     * wants: narrowing the Charge Master's own views to a billing desk would hide
     * services from the people who maintain them.
     */
    billingDesk: zod_1.z.enum(['OP', 'IP']).optional(),
    departmentId: uuid.optional(),
    categoryId: uuid.optional(),
    /**
     * Narrows to the services filed under ONE account head — what the catalog's
     * Account Heads tab drills into. Still metadata: an account head says which
     * income head a service belongs to, never what it costs.
     */
    accountHeadId: uuid.optional(),
    /**
     * Only SERVICE MASTER services — those that carry a serv code, and therefore
     * an identifier like `CFE369`.
     *
     * This is what the catalog's Service Code view lists, and it is deliberately
     * narrower than the unfiltered catalogue: that also holds legacy charges with
     * no identifier at all, which is precisely the distinction the view exists to
     * draw.
     */
    hasIdentifier: zod_1.z.coerce.boolean().optional(),
    /**
     * Comma-separated identifier PREFIXES — `XR` matches XR001, XRA010, XRC…
     *
     * How the Lab and Radiology views are expressed. An identifier is
     * `servCode + servNumber`, so a prefix on the identifier is a prefix on the
     * serv code, which is what the filter actually applies. A family is a naming
     * convention here, not a column: nothing in the schema says "XR means
     * radiology", and inventing such a column would freeze one hospital's
     * convention into everyone's database.
     */
    identifierPrefix: zod_1.z.string().max(60).optional(),
    barcode: zod_1.z.string().max(100).optional(),
    limit: zod_1.z.coerce.number().int().positive().max(50).optional(),
});
/**
 * Doctors billable against ONE department — the per-line doctor picker.
 *
 * `departmentId` is optional: a service with no department at all asks for the
 * unfiltered list rather than being shown an empty picker.
 */
exports.opBillDoctorQuerySchema = zod_1.z.object({
    departmentId: zod_1.z.string().uuid().optional(),
});
/** The Register's filter set. Every field maps to an indexed column (§6.1). */
exports.listOpBillsQuerySchema = zod_1.z.object({
    fromDate: zod_1.z.string().date().optional(),
    toDate: zod_1.z.string().date().optional(),
    patientId: uuid.optional(),
    doctorId: uuid.optional(),
    departmentId: uuid.optional(),
    payerId: uuid.optional(),
    counterId: uuid.optional(),
    cashierUserId: uuid.optional(),
    status: zod_1.z.enum(['OPEN', 'PARTIALLY_PAID', 'PAID', 'OVERPAID', 'WRITTEN_OFF', 'CANCELLED', 'REVERSED']).optional(),
    refundState: zod_1.z.enum(['NONE', 'PARTIAL', 'FULL']).optional(),
    /**
     * THE CREDIT REGISTER — desk, 2026-09-11: "this CROP register should see
     * separate tab named Credit Register after bill register." A bill passed
     * to credit is numbered out of its own `CROP…` series at posting time
     * (`OpInvoiceBuilder.build`'s `onCredit`); this asks the register for that
     * series only. An advance can never answer it and is excluded exactly like
     * a doctor/department/payer/status/refund filter (`register.queries.ts`).
     */
    onCredit: zod_1.z.coerce.boolean().optional(),
    /**
     * THE PRIVATE PATIENT REGISTER — desk, 2026-09-25: "need to maintain
     * private patient register under cash counter should show history." TRUE
     * = walk-ins only (`patientId IS NULL`); FALSE = patient-identified bills
     * only; absent = both, today's behaviour.
     */
    isWalkIn: zod_1.z.coerce.boolean().optional(),
    limit: zod_1.z.coerce.number().int().positive().max(200).default(50),
    /**
     * THE SERVER'S OWN CURSOR, OPAQUE — never a bare id.
     *
     * This read `uuid.optional()`, and that was a live defect rather than a
     * tightening: the register's cursor is base64url of `${sortValue}|${id}`
     * (`encodeCursor`, `register.queries.ts`) because `invoiceDate` is a DATE and
     * hundreds of bills share one, so a date-only cursor loops inside a day. A
     * composite cursor is not a UUID, so EVERY page after the first was rejected
     * here with "Invalid query parameters" before it reached the query — reported
     * from the desk on 2026-09-08 ("when given next page not loading") and again
     * on 2026-09-09 with the register showing its error state.
     *
     * Validated as an opaque bounded string, which is the only thing a client can
     * honestly assert about a token it did not mint: `decodeCursor` is what
     * decides whether it is well-formed, and it already rejects a malformed one
     * with `BadRequestError('Malformed cursor')`. A length cap keeps an unbounded
     * string out of the query planner.
     */
    cursor: zod_1.z.string().min(1).max(300).optional(),
});
/**
 * A BILL-LEVEL DISCOUNT — the counter's concession on the whole draft.
 *
 * EXACTLY ONE OF THE TWO, refined below. Both would give one bill two
 * discounts, and whichever the code read first would silently win.
 *
 * This is the second deliberate exception to this file's rule 1 ("no amount is
 * ever an input"), and it is a different KIND of figure from a price: it is
 * what the hospital chooses to give up, not what a service costs. The server
 * still decides whether the operator may give it (the policy ceiling in
 * `OpBillingConfig.maxDiscountPercentWithoutApproval`), still apportions it
 * across the lines so per-line GST stays correct, and still refuses to post a
 * bill whose discount is awaiting approval. Nothing about the PRICE becomes a
 * client input.
 */
exports.requestBillDiscountRequestSchema = zod_1.z
    .object({
    /** Of the bill's gross. Two decimals, matching the column. */
    percent: zod_1.z.number().min(0).max(100).optional(),
    /** Paise. Rejected above the bill's gross — a discount larger than the bill is not a discount. */
    amountPaise: zod_1.z.number().int().nonnegative().max(100_000_000).optional(),
    reason: zod_1.z.string().max(500).optional(),
})
    .refine((value) => (value.percent == null) !== (value.amountPaise == null), {
    message: 'Send a percent or an amount, never both and never neither',
    path: ['percent'],
});
/**
 * Apply the staff discount. AN EMPLOYEE, AND NOTHING ELSE.
 *
 * The absence of an amount here is the feature: the admin-set rule decides what
 * comes off, so the counter has no figure to send and none to edit.
 */
/**
 * WHO the concession is for, alongside WHICH employee it names.
 *
 * The amount is still never sent — that is the whole design of this endpoint
 * (see `StaffDiscountService`): the browser transmits an employee id and a
 * category, and the SERVER decides the figure from the rule. There is no field
 * to tamper with and no way for the screen and the invoice to disagree.
 *
 * `category` is optional and defaults to STAFF on the server, so a caller that
 * predates the other three categories keeps its exact behaviour.
 *
 * `employeeId` IS NOW OPTIONAL, and `student` is new — desk, 2026-09-17: a
 * Student concession has no employee to name, because there is no student
 * directory in this system, only the `Employee` master. The refinement below
 * is what keeps the two from being confused with each other: STAFF and
 * DEPENDENT still require a real employee id (unchanged); STUDENT and, since
 * 2026-09-23, RETIRED require `student` instead — a retired employee's own
 * Employee row is routinely marked inactive, which is what "retired" means,
 * so the desk asked to type the name rather than fight a picker over it. A
 * caller cannot name an employee for either free-text category and cannot
 * free-text a name for STAFF or DEPENDENT.
 *
 * ONLY THE NAME IS REQUIRED — desk decision, 2026-09-22, revising the
 * 2026-09-17 ask above: department/section turned out to be friction the
 * counter did not want for every student concession, so they are now
 * optional. `name` alone is still mandatory: a concession naming NOBODY is
 * the exact unaccountable-write-off shape `StaffDiscountService`'s own doc
 * comment on `requiresEmployee` exists to prevent, and a Student concession
 * has no employee id to fall back on as the "who".
 *
 * `doctorUserId` is OPTIONAL and consultation-fee-specific: naming the doctor
 * this concession's line belongs to lets the server recognise the one doctor
 * (C.M.O., Casualty) whose consultation is waived in full rather than
 * discounted by the standard rule — see `StaffDiscountService.findConsultationOffer`.
 * Universal Billing never sends it, and every line there resolves exactly as
 * it always has.
 */
/**
 * Categories named by free text, not by an Employee lookup.
 *
 * STUDENT never had a directory to search. RETIRED has an Employee row, but
 * it is routinely marked inactive — that is what "retired" means — and the
 * desk's own instruction (2026-09-23) was to type the name rather than make
 * the counter fight a picker that may or may not still carry them. CAMP
 * (2026-10-01) has no directory either — see `STAFF_BENEFICIARY_CATEGORIES`.
 * All three are named the same way as a result: by the `student` field below,
 * which is historically named for the category that first needed it but is,
 * and always was, a generic free-text identity — see `ConcessionStudent`.
 */
const FREE_TEXT_CATEGORIES = ['STUDENT', 'RETIRED', 'CAMP'];
/**
 * EVERY concession category the counter can send, in the order the dropdown
 * lists them. One list, because the apply request and the consultation preview
 * query below used to each spell out the same four literals and would have had
 * to be kept in step by hand.
 *
 * CAMP — desk, 2026-10-01: a concession given to a health camp rather than to
 * a person on any master. There is no camp directory to look up, so it is named
 * by typing the camp, exactly as a Student or Retired staff member is (see
 * `FREE_TEXT_CATEGORIES`). The amount still comes from the rule: a rule with no
 * `beneficiaryCategory` covers Camp like the other categories, and one naming
 * `CAMP` outranks it.
 */
exports.STAFF_BENEFICIARY_CATEGORIES = ['STAFF', 'STUDENT', 'DEPENDENT', 'RETIRED', 'CAMP'];
exports.applyStaffDiscountRequestSchema = zod_1.z
    .object({
    employeeId: uuid.optional(),
    student: zod_1.z
        .object({
        name: zod_1.z.string().trim().min(1, 'The name is required'),
        department: zod_1.z.string().trim().optional(),
        section: zod_1.z.string().trim().optional(),
    })
        .optional(),
    category: zod_1.z.enum(exports.STAFF_BENEFICIARY_CATEGORIES).optional(),
    doctorUserId: uuid.optional(),
    /**
     * Staff DEPENDENT only (desk, 2026-10-12): how the patient is related to the
     * staff member named by `employeeId`. The Cash Counter must send it and the
     * server refuses a Dependent concession without it there; it is optional in
     * the SHAPE so the Consultation Fee dialog, which has never sent one, is
     * unchanged. A son or daughter is checked against the patient's date of birth.
     */
    relation: zod_1.z.enum(staff_concession_1.DEPENDENT_RELATIONS).optional(),
})
    .refine((body) => Boolean(body.employeeId) !== Boolean(body.student), {
    message: 'Name an employee, or give the free-text name — never both, never neither',
    path: ['employeeId'],
})
    .refine((body) => !body.student || FREE_TEXT_CATEGORIES.includes(body.category), {
    message: 'Free-text details are only for the Student, Retired staff or Camp category',
    path: ['student'],
})
    .refine((body) => !FREE_TEXT_CATEGORIES.includes(body.category) || Boolean(body.student), {
    message: 'The Student, Retired staff and Camp categories are named by typing the name, not an employee id',
    path: ['student'],
})
    .refine((body) => !body.relation || body.category === 'DEPENDENT', {
    message: 'A relation is only for the staff Dependent category',
    path: ['relation'],
});
/**
 * The Concession's own employee finder: name or employee code. A route of its own
 * under `financial:discount:apply` so the picker works for everyone who may give a
 * concession — it used to borrow the IP-admission employee list, which needs an
 * unrelated IP permission.
 */
exports.staffConcessionEmployeeQuerySchema = zod_1.z.object({
    search: zod_1.z.string().trim().max(100).optional(),
});
/**
 * Which concession a doctor's OWN consultation would carry — the READ behind
 * the Concession tab's preview, before anything is applied. Same category
 * enum as the apply request; no employee or student here, because a preview
 * does not commit to a beneficiary.
 */
exports.consultationConcessionOfferQuerySchema = zod_1.z.object({
    category: zod_1.z.enum(exports.STAFF_BENEFICIARY_CATEGORIES),
});
/**
 * DECIDING SOMEBODY ELSE'S DISCOUNT — desk, 2026-08-31:
 *
 *   "when frontdesk is applied discount admin should approve this discount…
 *    instead of generate bill just give approve button. so admin give
 *    generate bill"
 *
 * WHY THESE EXIST NOW AND NOT BEFORE. `OpBillDiscountService` has carried
 * `approve()`, `reject()` and `findPending()` since Phase 2, fully written and
 * unit-tested — and NOTHING ever called them. There was no route, so a discount
 * that needed approval entered `REQUESTED` and stayed there for ever: it never
 * applied, and `assertNoPendingDiscounts` then refused to post the bill it was
 * attached to. The counter's only escape was to clear the field. These schemas
 * are the missing HTTP surface, not a new mechanism.
 *
 * `lockVersion` IS MANDATORY on both. The row carries a CAS column and two
 * approvers looking at the same queue is the ordinary case, not the exotic one
 * — without it the second decision silently overwrites the first, and a
 * rejection could be turned into an approval by a stale screen.
 */
exports.approvalIdParamSchema = zod_1.z.object({ approvalId: zod_1.z.string().uuid() });
/**
 * The approver may sanction LESS than was asked for, never more — the service
 * enforces that too, and this is the same rule stated at the boundary. Omitting
 * both figures approves exactly what was requested, which is the common case:
 * an approver who agrees does not want to retype the number.
 *
 * This is the third exception to this file's "no amount is ever an input" rule,
 * and it is the same KIND of figure as `requestBillDiscountRequestSchema`'s:
 * what the hospital chooses to give up, decided by a person with the authority
 * to give it up.
 */
exports.approveBillDiscountRequestSchema = zod_1.z.object({
    lockVersion: zod_1.z.number().int().nonnegative(),
    percent: zod_1.z.number().min(0).max(100).optional(),
    amountPaise: zod_1.z.number().int().nonnegative().max(100_000_000).optional(),
    reason: zod_1.z.string().max(500).optional(),
});
/**
 * A REASON IS REQUIRED to reject and optional to approve, and the asymmetry is
 * deliberate: the counter has to tell the patient something, and "refused" on
 * its own is not something anybody can repeat across a counter.
 */
exports.rejectBillDiscountRequestSchema = zod_1.z.object({
    lockVersion: zod_1.z.number().int().nonnegative(),
    reason: zod_1.z.string().min(1).max(500),
});
//# sourceMappingURL=op-billing.request.js.map